import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import appScriptService from '../utils/appScriptService';
import gcsService from '../utils/gcsService';

const Podcast = () => {
  const [selectedModule, setSelectedModule] = useState('LM2001020230');
  const [podcastEpisodes, setPodcastEpisodes] = useState([]);
  const [currentEpisode, setCurrentEpisode] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentAudio, setCurrentAudio] = useState(null);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [showScript, setShowScript] = useState(false);
  const [playbackRate, setPlaybackRate] = useState(1);

  // NCS 모듈 목록 (Interview.js와 동일)
  const modules = [
    { id: 'LM2001020230', name: '프로그래밍언어응용' },
    { id: 'LM2001020231', name: '프로그래밍언어활용' },
    { id: 'LM2001020224', name: '화면설계' },
    { id: 'LM2001020225', name: '화면구현' },
    { id: 'LM2001020211', name: '서버프로그램구현' },
    { id: 'LM2001020227', name: '애플리케이션테스트수행' },
    { id: 'LM2001020205', name: '데이터입출력구현' },
    { id: 'LM2001020206', name: '통합구현' },
    { id: 'LM2001020214', name: '애플리케이션배포' },
    { id: 'LM2001020212', name: '인터페이스구현' },
    // 추가 카테고리
    { id: 'BE001_SPRING', name: 'Spring 백엔드' },
    { id: 'FE001_REACT', name: 'React 프론트엔드' },
    { id: 'HR001_COMMON', name: '공통 면접질문' },
  ];

  // 컴포넌트 마운트 시 팟캐스트 에피소드 로드
  useEffect(() => {
    loadPodcastEpisodes();
  }, [selectedModule]);

  // 오디오 시간 업데이트
  useEffect(() => {
    const updateTime = () => {
      if (currentAudio) {
        setCurrentTime(currentAudio.currentTime);
        setDuration(currentAudio.duration || 0);
      }
    };

    if (currentAudio) {
      currentAudio.addEventListener('timeupdate', updateTime);
      currentAudio.addEventListener('loadedmetadata', updateTime);
      
      return () => {
        currentAudio.removeEventListener('timeupdate', updateTime);
        currentAudio.removeEventListener('loadedmetadata', updateTime);
      };
    }
  }, [currentAudio]);

  // 탭 변경 시 오디오 일시정지 처리
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden && currentAudio && isPlaying) {
        currentAudio.pause();
        setIsPlaying(false);
        console.log('🔇 탭 변경으로 오디오 일시정지');
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [currentAudio, isPlaying]);

  // 팟캐스트 에피소드 로드 (TTS_Scripts 시트에서 직접 조회)
  const loadPodcastEpisodes = async () => {
    setIsLoading(true);
    // 이전 데이터 즉시 클리어
    setPodcastEpisodes([]);
    setCurrentEpisode(null);
    setIsPlaying(false);
    if (currentAudio) {
      currentAudio.pause();
      setCurrentAudio(null);
    }
    
    try {

      
      // TTS_Scripts 시트에서 데이터 조회
      const ttsData = await appScriptService.getTTSScripts(selectedModule);
      

      
      if (!ttsData || !ttsData.sections || ttsData.sections.length === 0) {

        setPodcastEpisodes([]);
        return;
      }

      // TTS 데이터를 팟캐스트 에피소드 형식으로 변환
      const podcastEpisodes = ttsData.sections.map((section, index) => ({
        course_id: selectedModule,
        course_name: modules.find(m => m.id === selectedModule)?.name || selectedModule,
        course_title: ttsData.title,
        section_order: index + 1,
        section_title: section.title,
        section_content: section.content,
        audio_url: null, // GCS 처리에서 생성될 예정
        _hasAudio: false
      }));



      // GCS Signed URL 처리로 오디오 URL 생성
      const processedEpisodes = await gcsService.processPodcastEpisodes(podcastEpisodes);
      
      setPodcastEpisodes(processedEpisodes);


      // 오디오가 있는 에피소드 개수 로깅
      const audioCount = processedEpisodes.filter(ep => ep._hasAudio).length;


    } catch (error) {
      console.error('❌ 팟캐스트 로드 실패:', error);
      setPodcastEpisodes([]);
    } finally {
      setIsLoading(false);
    }
  };

  // 오디오 재생/일시정지 토글
  const togglePlayPause = async (episode) => {
    if (currentAudio && currentEpisode?.section_order === episode.section_order) {
      // 같은 에피소드 - 재생/일시정지 토글
      if (isPlaying) {
        currentAudio.pause();
        setIsPlaying(false);
      } else {
        try {
          await currentAudio.play();
          setIsPlaying(true);
        } catch (error) {
          console.error('오디오 재생 재시작 실패:', error);
          alert('오디오 재생 재시작에 실패했습니다.');
        }
      }
    } else {
      // 다른 에피소드 - 새로 재생
      if (currentAudio) {
        currentAudio.pause();
        currentAudio.currentTime = 0;
        setCurrentAudio(null);
        setIsPlaying(false);
      }
      
      if (episode.audio_url) {

        
        // CORS 문제를 우회하기 위해 프록시 방식으로 변경
        const audio = new Audio();
        audio.preload = 'metadata';
        audio.playbackRate = playbackRate;
        
        // 브라우저 지원 오디오 형식 체크
        const canPlayMP3 = audio.canPlayType('audio/mpeg');
        const canPlayOGG = audio.canPlayType('audio/ogg');
        const canPlayWAV = audio.canPlayType('audio/wav');
        
        console.log('브라우저 오디오 지원:', {
          mp3: canPlayMP3,
          ogg: canPlayOGG,
          wav: canPlayWAV
        });
        
        if (!canPlayMP3 && canPlayMP3 !== 'probably' && canPlayMP3 !== 'maybe') {
          console.warn('⚠️ 브라우저가 MP3를 지원하지 않을 수 있습니다');
        }
        
        // 이벤트 리스너들
        const handleLoadedMetadata = () => {

          setDuration(audio.duration || 0);
        };
        
        const handleCanPlay = () => {

        };
        
        const handleEnded = () => {

          setIsPlaying(false);
          playNextEpisodeAuto(episode);
        };
        
        const handleError = (e) => {
          console.error('❌ 오디오 재생 오류:', e);
          console.error('오디오 에러 상세:', {
            error: audio.error,
            networkState: audio.networkState,
            readyState: audio.readyState,
            src: audio.src,
            currentTime: audio.currentTime,
            duration: audio.duration
          });
          
          // 네트워크 상태 매핑
          const networkStates = {
            0: 'NETWORK_EMPTY - 아직 초기화되지 않음',
            1: 'NETWORK_IDLE - 활성 네트워크 없음',
            2: 'NETWORK_LOADING - 데이터 로딩 중',
            3: 'NETWORK_NO_SOURCE - 네트워크 소스 없음'
          };
          
          const readyStates = {
            0: 'HAVE_NOTHING - 데이터 없음',
            1: 'HAVE_METADATA - 메타데이터만 있음',
            2: 'HAVE_CURRENT_DATA - 현재 위치 데이터만 있음',
            3: 'HAVE_FUTURE_DATA - 현재와 미래 데이터 있음',
            4: 'HAVE_ENOUGH_DATA - 재생 가능한 충분한 데이터'
          };
          
          console.error('네트워크 상태:', networkStates[audio.networkState] || audio.networkState);
          console.error('준비 상태:', readyStates[audio.readyState] || audio.readyState);
          
          let errorMessage = '오디오 재생 중 오류가 발생했습니다.';
          if (audio.error) {
            switch (audio.error.code) {
              case 1: errorMessage = '오디오 로드가 중단되었습니다.'; break;
              case 2: 
                errorMessage = '네트워크 오류가 발생했습니다. GCS 연결을 확인해주세요.';
                console.error('네트워크 에러 - URL을 브라우저에서 직접 확인해보세요:', audio.src);
                break;
              case 3: errorMessage = '오디오 디코딩 오류가 발생했습니다.'; break;
              case 4: 
                errorMessage = '지원하지 않는 오디오 형식입니다.';
                console.error('지원하지 않는 형식 - MIME 타입 확인 필요');
                break;
              default: errorMessage = `오디오 에러 (코드: ${audio.error.code})`;
            }
          }
          
          // 재시도 로직 제거 - 오디오 객체 오류만 처리
          
          console.error('최종 오디오 에러 메시지:', errorMessage);
          alert(errorMessage);
          setIsPlaying(false);
        };
        
        const handleLoadStart = () => {

        };
        
        const handleProgress = () => {
          if (audio.buffered.length > 0) {
            const bufferedEnd = audio.buffered.end(audio.buffered.length - 1);
            const duration = audio.duration;
            if (duration > 0) {
              const bufferedPercent = (bufferedEnd / duration) * 100;
              if (bufferedPercent > 10) { // 10% 이상 버퍼링될 때만 로그

              }
            }
          }
        };
        
        const handleTimeUpdate = () => {
          setCurrentTime(audio.currentTime);
        };
        
        // 이벤트 리스너 등록
        audio.addEventListener('loadedmetadata', handleLoadedMetadata);
        audio.addEventListener('canplay', handleCanPlay);
        audio.addEventListener('ended', handleEnded);
        audio.addEventListener('error', handleError);
        audio.addEventListener('loadstart', handleLoadStart);
        audio.addEventListener('progress', handleProgress);
        audio.addEventListener('timeupdate', handleTimeUpdate);
        
        // URL 설정 및 로드 (지연 처리)
        audio.src = episode.audio_url;
        
        setCurrentAudio(audio);
        setCurrentEpisode(episode);
        
        // 오디오 로딩 대기 및 재시도 로직
        const waitForAudioReady = async (retries = 3, delay = 1000) => {
          for (let i = 0; i < retries; i++) {
            try {

              
              // 짧은 대기 후 재생 시도
              if (i > 0) {

                await new Promise(resolve => setTimeout(resolve, delay));
              }
              
              // 오디오 로딩 상태 확인
              if (audio.readyState >= 2) { // HAVE_CURRENT_DATA 이상

              } else {

                
                // loadeddata 이벤트 대기
                await new Promise((resolve, reject) => {
                  const timeout = setTimeout(() => {
                    reject(new Error('로딩 타임아웃'));
                  }, 5000);
                  
                  const onLoadedData = () => {
                    clearTimeout(timeout);
                    audio.removeEventListener('loadeddata', onLoadedData);

                    resolve();
                  };
                  
                  audio.addEventListener('loadeddata', onLoadedData);
                });
              }
              
              await audio.play();
              setIsPlaying(true);

              return; // 성공 시 함수 종료
              
            } catch (error) {
              console.error(`❌ 재생 시도 ${i + 1} 실패:`, error.message);
              
              if (i === retries - 1) {
                // 마지막 시도 실패
                throw error;
              }
              
              // 다음 시도를 위한 대기 시간 증가
              delay *= 1.5;
            }
          }
        };
        
        try {
          await waitForAudioReady();
        } catch (error) {
          console.error('❌ 오디오 재생 실패:', error);
          
          // 브라우저 정책에 의한 자동재생 차단인지 확인
          if (error.name === 'NotAllowedError') {
            alert('브라우저에서 자동재생을 차단했습니다. 재생 버튼을 다시 클릭해주세요.');
          } else if (error.name === 'NotSupportedError') {
            alert('지원하지 않는 오디오 형식입니다.');
          } else if (error.message.includes('network')) {
            alert('네트워크 오류가 발생했습니다. 인터넷 연결을 확인해주세요.');
          } else {
            alert(`오디오 재생에 실패했습니다: ${error.message}`);
          }
          setIsPlaying(false);
          setCurrentAudio(null);
          setCurrentEpisode(null);
        }
      } else {
        console.error('❌ 오디오 URL이 없습니다:', episode);
        alert('이 에피소드의 오디오 파일을 찾을 수 없습니다.');
      }
    }
  };

  // 다음 에피소드 재생 (수동 버튼 클릭)
  const playNextEpisode = () => {
    if (!currentEpisode) return;
    
    const currentIndex = podcastEpisodes.findIndex(ep => ep.section_order === currentEpisode.section_order);
    const nextIndex = currentIndex + 1;
    
    if (nextIndex < podcastEpisodes.length) {
      const nextEpisode = podcastEpisodes[nextIndex];
      togglePlayPause(nextEpisode);
    } else {
      // 마지막 에피소드 완료
      setCurrentEpisode(null);
      setIsPlaying(false);
    }
  };

  // 자동 다음 에피소드 재생 (오디오 종료 시)
  const playNextEpisodeAuto = (finishedEpisode) => {

    
    const currentIndex = podcastEpisodes.findIndex(ep => ep.section_order === finishedEpisode.section_order);
    const nextIndex = currentIndex + 1;
    
    if (nextIndex < podcastEpisodes.length) {
      const nextEpisode = podcastEpisodes[nextIndex];

      
      // 약간의 지연 후 다음 에피소드 재생 (사용자 경험 향상)
      setTimeout(() => {
        togglePlayPause(nextEpisode);
      }, 500);
    } else {
      // 마지막 에피소드 완료

      setCurrentEpisode(null);
      setIsPlaying(false);
    }
  };

  // 이전 에피소드 재생
  const playPreviousEpisode = () => {
    if (!currentEpisode) return;
    
    const currentIndex = podcastEpisodes.findIndex(ep => ep.section_order === currentEpisode.section_order);
    const prevIndex = currentIndex - 1;
    
    if (prevIndex >= 0) {
      const prevEpisode = podcastEpisodes[prevIndex];
      togglePlayPause(prevEpisode);
    }
  };

  // 오디오 중지
  const stopAudio = () => {
    if (currentAudio) {
      currentAudio.pause();
      currentAudio.currentTime = 0;
      setCurrentAudio(null);
    }
    setIsPlaying(false);
    setCurrentEpisode(null);
    setCurrentTime(0);
    setDuration(0);
  };

  // 재생 속도 변경
  const changePlaybackRate = (rate) => {
    setPlaybackRate(rate);
    if (currentAudio) {
      currentAudio.playbackRate = rate;
    }
  };

  // 진행률 계산
  const getProgress = () => {
    if (!duration) return 0;
    return (currentTime / duration) * 100;
  };

  // 시간 포맷팅
  const formatTime = (seconds) => {
    if (!seconds || isNaN(seconds)) return '00:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // 선택된 모듈 정보
  const getSelectedModuleInfo = () => {
    return modules.find(module => module.id === selectedModule) || modules[0];
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50 py-8">
      <div className="max-w-4xl mx-auto px-4">
        {/* 헤더 */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <h1 className="text-4xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-4">
            🎧 NCS 팟캐스트 학습
          </h1>
          <p className="text-gray-600 text-lg">
            AI 강사의 상세한 해설로 완벽하게 학습하세요
          </p>
        </motion.div>

        {/* 모듈 선택 */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-2xl shadow-lg p-6 mb-6"
        >
          <h2 className="text-2xl font-semibold mb-4 flex items-center">
            📚 학습 모듈 선택
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                팟캐스트 모듈
              </label>
              <select
                value={selectedModule}
                onChange={(e) => {
                  // 모듈 변경 시 즉시 모든 데이터 클리어
                  setPodcastEpisodes([]);
                  setCurrentEpisode(null);
                  setIsPlaying(false);
                  setCurrentTime(0);
                  setDuration(0);
                  if (currentAudio) {
                    currentAudio.pause();
                    currentAudio.currentTime = 0;
                    setCurrentAudio(null);
                  }
                  setSelectedModule(e.target.value);
                }}
                disabled={isLoading}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {modules.map(module => (
                  <option key={module.id} value={module.id}>
                    {module.name}
                  </option>
                ))}
              </select>
            </div>
            
            <div className="flex flex-col justify-end">
              <div className="bg-blue-50 p-4 rounded-lg">
                <div className="text-sm text-gray-600">팟캐스트 상태</div>
                <div className="text-lg font-bold text-blue-600">
                  {isLoading ? '로딩 중...' : `${podcastEpisodes.length}개 에피소드`}
                </div>
                {currentEpisode && (
                  <div className="text-sm text-blue-500 mt-1">
                    재생 중: {currentEpisode.section_title}
                  </div>
                )}
              </div>
            </div>
          </div>
        </motion.div>

        {/* 현재 재생 중인 플레이어 */}
        {currentEpisode && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl shadow-lg p-6 mb-6 text-white"
          >
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-semibold">현재 재생 중</h3>
                <p className="text-blue-100">{currentEpisode.section_title}</p>
              </div>
              <div className="text-right">
                <div className="text-sm opacity-90">
                  {formatTime(currentTime)} / {formatTime(duration)}
                </div>
              </div>
            </div>

            {/* 진행 바 */}
            <div className="w-full bg-white bg-opacity-30 rounded-full h-2 mb-4">
              <div 
                className="bg-white h-2 rounded-full transition-all duration-300"
                style={{ width: `${getProgress()}%` }}
              ></div>
            </div>

            {/* 컨트롤 버튼 */}
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <button
                  onClick={playPreviousEpisode}
                  className="p-2 bg-white bg-opacity-20 rounded-full hover:bg-opacity-30 transition-colors"
                  disabled={!currentEpisode}
                >
                  ⏮️
                </button>
                <button
                  onClick={() => togglePlayPause(currentEpisode)}
                  className="p-3 bg-white bg-opacity-20 rounded-full hover:bg-opacity-30 transition-colors"
                >
                  {isPlaying ? '⏸️' : '▶️'}
                </button>
                <button
                  onClick={playNextEpisode}
                  className="p-2 bg-white bg-opacity-20 rounded-full hover:bg-opacity-30 transition-colors"
                  disabled={!currentEpisode}
                >
                  ⏭️
                </button>
                <button
                  onClick={stopAudio}
                  className="p-2 bg-white bg-opacity-20 rounded-full hover:bg-opacity-30 transition-colors"
                >
                  ⏹️
                </button>
              </div>

              {/* 재생 속도 */}
              <div className="flex items-center space-x-2">
                <span className="text-sm opacity-90">속도:</span>
                {[0.75, 1, 1.25, 1.5].map(rate => (
                  <button
                    key={rate}
                    onClick={() => changePlaybackRate(rate)}
                    className={`px-2 py-1 rounded text-xs ${
                      playbackRate === rate 
                        ? 'bg-white text-blue-600' 
                        : 'bg-white bg-opacity-20 hover:bg-opacity-30'
                    }`}
                  >
                    {rate}x
                  </button>
                ))}
              </div>
            </div>
          </motion.div>
        )}

        {/* 에피소드 리스트 */}
        {isLoading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">팟캐스트 에피소드를 불러오는 중...</p>
          </div>
        ) : podcastEpisodes.length > 0 ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="space-y-4"
          >
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-2xl font-semibold">
                📖 {getSelectedModuleInfo().name} 에피소드
              </h2>
              <button
                onClick={() => setShowScript(!showScript)}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
              >
                {showScript ? '스크립트 숨기기' : '스크립트 보기'}
              </button>
            </div>

            {isLoading && (
              <div className="text-center py-8">
                <div className="animate-spin inline-block w-8 h-8 border-4 border-current border-t-transparent text-blue-600 rounded-full" role="status" aria-label="loading">
                  <span className="sr-only">Loading...</span>
                </div>
                <p className="mt-2 text-gray-600">에피소드를 불러오는 중...</p>
              </div>
            )}

            {!isLoading && podcastEpisodes.map((episode, index) => (
              <motion.div
                key={episode.section_order}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 * index }}
                className={`bg-white rounded-xl shadow-lg p-6 ${
                  currentEpisode?.section_order === episode.section_order
                    ? 'ring-2 ring-blue-500 ring-opacity-50'
                    : ''
                }`}
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center">
                    <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full flex items-center justify-center text-white font-bold mr-4">
                      {episode.section_order}
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-800">
                        {episode.section_title}
                      </h3>
                      <div className="flex items-center text-sm text-gray-500 mt-1">
                        {episode._hasAudio ? (
                          <>
                            <span className="text-green-500 mr-2">🎵</span>
                            오디오 사용 가능
                          </>
                        ) : (
                          <>
                            <span className="text-red-500 mr-2">🚫</span>
                            오디오 없음
                          </>
                        )}
                      </div>
                    </div>
                  </div>

                  <button
                    onClick={() => togglePlayPause(episode)}
                    disabled={!episode._hasAudio}
                    className={`px-6 py-3 rounded-lg font-semibold transition-colors ${
                      episode._hasAudio
                        ? currentEpisode?.section_order === episode.section_order && isPlaying
                          ? 'bg-red-500 text-white hover:bg-red-600'
                          : 'bg-blue-500 text-white hover:bg-blue-600'
                        : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    }`}
                  >
                    {episode._hasAudio ? (
                      currentEpisode?.section_order === episode.section_order && isPlaying
                        ? '⏸️ 일시정지'
                        : '▶️ 재생'
                    ) : '오디오 없음'}
                  </button>
                </div>

                {/* 스크립트 */}
                {showScript && (
                  <div className="mt-4 p-4 bg-gray-50 rounded-lg border-l-4 border-blue-400">
                    <h4 className="font-semibold text-gray-800 mb-2">📝 스크립트</h4>
                    <p className="text-gray-700 leading-relaxed whitespace-pre-line">
                      {episode.section_content}
                    </p>
                  </div>
                )}
              </motion.div>
            ))}
          </motion.div>
        ) : (
          <div className="text-center py-12">
            <div className="text-6xl mb-4">📭</div>
            <h3 className="text-xl font-semibold text-gray-800 mb-2">
              팟캐스트 에피소드가 없습니다
            </h3>
            <p className="text-gray-600">
              선택한 모듈의 팟캐스트 콘텐츠가 준비되지 않았습니다.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Podcast;